import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api, type LogSource, type LogLine } from '../api';
import { Page, Card, Button, Field, Input, Modal, EmptyState, Badge } from '../ui';

export function Logs() {
  const { slug } = useParams<{ slug: string }>();
  const [sources, setSources] = useState<LogSource[]>([]);
  const [defaultTtl, setDefaultTtl] = useState(24);
  const [selected, setSelected] = useState<string | null>(null);
  const [lines, setLines] = useState<LogLine[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  // filtros
  const [day, setDay] = useState('');
  const [q, setQ] = useState('');
  const [auto, setAuto] = useState(true);

  // criar fonte
  const [creating, setCreating] = useState(false);
  const [fName, setFName] = useState('');
  const [fContainer, setFContainer] = useState('');
  const [fTtl, setFTtl] = useState('');

  function loadSources() {
    if (!slug) return;
    api
      .listLogSources(slug)
      .then(({ sources, defaultTtlHours }) => {
        setSources(sources);
        setDefaultTtl(defaultTtlHours);
        if (!selected && sources.length) setSelected(sources[0]!.id);
      })
      .catch(() => setErr('Falha ao carregar fontes'))
      .finally(() => setLoading(false));
  }

  function loadLines() {
    if (!slug || !selected) {
      setLines([]);
      return;
    }
    api
      .getLogLines(slug, selected, { day: day || undefined, q: q || undefined, limit: 1000, order: 'desc' })
      .then(({ lines }) => setLines(lines))
      .catch(() => setLines([]));
  }

  useEffect(() => {
    loadSources();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  useEffect(() => {
    loadLines();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, day, q]);

  useEffect(() => {
    if (!auto || day) return; // não auto-atualiza quando filtrando por dia fixo
    const t = setInterval(loadLines, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auto, selected, day, q]);

  async function createSource() {
    if (!slug || !fName.trim() || !fContainer.trim()) return;
    try {
      const { source } = await api.createLogSource(slug, {
        name: fName.trim(),
        containerName: fContainer.trim(),
        ttlHours: fTtl ? Number(fTtl) : null,
      });
      setCreating(false);
      setFName('');
      setFContainer('');
      setFTtl('');
      setSelected(source.id);
      loadSources();
    } catch {
      setErr('Falha ao criar fonte (container já cadastrado?)');
    }
  }

  async function removeSource(id: string) {
    if (!slug || !confirm('Remover esta fonte de log? As linhas coletadas serão apagadas.')) return;
    await api.deleteLogSource(slug, id).catch(() => undefined);
    if (selected === id) setSelected(null);
    loadSources();
  }

  return (
    <Page
      title="Logs"
      subtitle={`Logs das aplicações coletados pelo Neo-Pilots. Retenção padrão ${defaultTtl}h (cada fonte pode sobrescrever).`}
      actions={<Button variant="primary" onClick={() => setCreating(true)}>+ Fonte</Button>}
    >
      {err && <div className="chat-error">{err}</div>}
      {loading ? (
        <div className="center">Carregando…</div>
      ) : sources.length === 0 ? (
        <EmptyState
          title="Nenhuma fonte de log"
          description="Cadastre um container (ex: bugpilot-api) para o Neo-Pilots coletar os logs dele."
          action={<Button variant="primary" onClick={() => setCreating(true)}>+ Fonte</Button>}
        />
      ) : (
        <div className="logs-layout">
          <aside className="logs-sources">
            {sources.map((s) => (
              <div key={s.id} className={`logs-source-row ${s.id === selected ? 'is-active' : ''}`}>
                <button type="button" className="logs-source-label" onClick={() => setSelected(s.id)}>
                  <div className="logs-source-name">{s.name} {!s.enabled && <Badge variant="neutral">off</Badge>}</div>
                  <div className="users-muted" style={{ fontSize: 11 }}>
                    {s.containerName} · TTL {s.ttlHours ?? defaultTtl}h
                  </div>
                </button>
                <button className="docs-tree-actions" onClick={() => removeSource(s.id)} title="remover">×</button>
              </div>
            ))}
          </aside>

          <Card className="logs-view">
            <div className="logs-toolbar">
              <Field label="Dia"><Input type="date" value={day} onChange={(e) => setDay(e.target.value)} /></Field>
              <Field label="Buscar"><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="texto no log…" /></Field>
              <label className="conv-auto"><input type="checkbox" checked={auto} onChange={(e) => setAuto(e.target.checked)} /> ao vivo</label>
              <Button size="sm" variant="ghost" onClick={loadLines}>atualizar</Button>
            </div>
            <div className="logs-lines">
              {lines.length === 0 ? (
                <div className="users-muted" style={{ padding: 12 }}>Sem linhas no período. (a coleta roda a cada ~30s)</div>
              ) : (
                lines.map((l) => (
                  <div key={l.id} className="logs-line">
                    <span className="logs-line-ts">{new Date(l.ts).toLocaleString()}</span>
                    <span className="logs-line-msg">{l.message}</span>
                  </div>
                ))
              )}
            </div>
          </Card>
        </div>
      )}

      <Modal
        open={creating}
        onClose={() => setCreating(false)}
        title="Nova fonte de log"
        footer={
          <>
            <Button variant="ghost" onClick={() => setCreating(false)}>cancelar</Button>
            <Button variant="primary" onClick={createSource} disabled={!fName.trim() || !fContainer.trim()}>criar</Button>
          </>
        }
      >
        <Field label="Nome"><Input value={fName} onChange={(e) => setFName(e.target.value)} placeholder="API (prod)" autoFocus /></Field>
        <Field label="Container (no host)"><Input value={fContainer} onChange={(e) => setFContainer(e.target.value)} placeholder="bugpilot-api" /></Field>
        <Field label="TTL (horas, vazio = padrão)" hint={`Padrão: ${defaultTtl}h`}>
          <Input type="number" value={fTtl} onChange={(e) => setFTtl(e.target.value)} placeholder={String(defaultTtl)} />
        </Field>
      </Modal>
    </Page>
  );
}
